/**
 * CycleToolbar — step through sweep cases sorted by a chosen variable.
 *
 * Provides: variable picker, prev/next arrows, play/pause with speed
 * control, and a position indicator showing the current variable value.
 */

import { memo, useCallback, useEffect, useRef, type CSSProperties } from "react";

export interface CycleStep {
  nodeId: string;
  value: number;
}

export interface CycleToolbarProps {
  /** Numeric column names available for cycling (e.g. "alpha_deg", "mach"). */
  variables: string[];
  /** Currently selected cycle variable, or null if cycling is off. */
  activeVariable: string | null;
  onVariableChange: (variable: string | null) => void;
  /** Ordered steps for the active variable. */
  steps: CycleStep[];
  /** Current index into `steps`. */
  currentIdx: number;
  onStep: (idx: number) => void;
  /** Playback state. */
  playing: boolean;
  onPlayToggle: () => void;
  /** Interval in ms between steps during playback. */
  speed: number;
  onSpeedChange: (ms: number) => void;
  /** Whether the viewer is currently loading a case. */
  loading: boolean;
}

const SPEEDS: { label: string; ms: number }[] = [
  { label: "1s", ms: 1000 },
  { label: "2s", ms: 2000 },
  { label: "3s", ms: 3000 },
  { label: "5s", ms: 5000 },
];

const TB: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  fontSize: 11,
  borderBottom: "1px solid var(--gray-a4)",
  background: "var(--color-surface, #1a1a2e)",
  flexWrap: "wrap",
};

const SEL: CSSProperties = {
  padding: "2px 4px",
  border: "1px solid var(--gray-a6)",
  borderRadius: 4,
  background: "var(--color-surface, #1a1a2e)",
  color: "var(--gray-12)",
  fontSize: 11,
  maxWidth: 140,
};

const ARROW: CSSProperties = {
  padding: "2px 8px",
  border: "1px solid var(--gray-a6)",
  borderRadius: 4,
  background: "var(--gray-a3)",
  color: "var(--gray-12)",
  fontSize: 13,
  cursor: "pointer",
  lineHeight: 1,
  fontWeight: 600,
};

const PLAY: CSSProperties = {
  ...ARROW,
  fontSize: 11,
  fontWeight: 500,
  minWidth: 40,
  textAlign: "center" as const,
};

const POS: CSSProperties = {
  color: "var(--gray-11)",
  whiteSpace: "nowrap",
  minWidth: 60,
  textAlign: "center" as const,
};

const LABEL: CSSProperties = {
  color: "var(--gray-11)",
  whiteSpace: "nowrap",
};

export const CycleToolbar = memo(function CycleToolbar({
  variables,
  activeVariable,
  onVariableChange,
  steps,
  currentIdx,
  onStep,
  playing,
  onPlayToggle,
  speed,
  onSpeedChange,
  loading,
}: CycleToolbarProps) {
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const currentIdxRef = useRef(currentIdx);
  currentIdxRef.current = currentIdx;
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const goPrev = useCallback(() => {
    if (stepsRef.current.length === 0) return;
    const next = (currentIdxRef.current - 1 + stepsRef.current.length) % stepsRef.current.length;
    onStep(next);
  }, [onStep]);

  const goNext = useCallback(() => {
    if (stepsRef.current.length === 0) return;
    const next = (currentIdxRef.current + 1) % stepsRef.current.length;
    onStep(next);
  }, [onStep]);

  // Auto-play timer
  useEffect(() => {
    if (!playing || steps.length === 0) return;
    const id = setInterval(() => {
      if (loadingRef.current) return; // wait for current case to finish
      const next = (currentIdxRef.current + 1) % stepsRef.current.length;
      onStep(next);
    }, speed);
    return () => clearInterval(id);
  }, [playing, speed, steps.length, onStep]);

  if (variables.length === 0) return null;

  const step = steps[currentIdx];

  return (
    <div style={TB} className="f3v-cycle-toolbar">
      <span style={LABEL}>Cycle</span>
      <select
        value={activeVariable ?? ""}
        onChange={(e) => onVariableChange(e.target.value || null)}
        style={SEL}
      >
        <option value="">Off</option>
        {variables.map((v) => (
          <option key={v} value={v}>{v}</option>
        ))}
      </select>

      {activeVariable && steps.length > 0 && (
        <>
          <button style={ARROW} onClick={goPrev} title="Previous" disabled={loading}>
            &#9664;
          </button>

          <span style={POS}>
            {step ? `${activeVariable} = ${Number(step.value.toPrecision(4))}` : "—"}
            <span style={{ color: "var(--gray-9)", marginLeft: 4 }}>
              ({currentIdx + 1}/{steps.length})
            </span>
          </span>

          <button style={ARROW} onClick={goNext} title="Next" disabled={loading}>
            &#9654;
          </button>

          <button style={PLAY} onClick={onPlayToggle} title={playing ? "Pause" : "Play"}>
            {playing ? "⏸" : "▶"}
          </button>

          <select
            value={speed}
            onChange={(e) => onSpeedChange(Number(e.target.value))}
            style={{ ...SEL, width: 50 }}
            title="Playback speed"
          >
            {SPEEDS.map((s) => (
              <option key={s.ms} value={s.ms}>{s.label}</option>
            ))}
          </select>
        </>
      )}
    </div>
  );
});
